import {
  AloftArea,
  DefaultEcoLifeField,
  EcoLife,
  HasFeatureAreaAndQuality,
  Improvements,
  ORDER_FEATURES,
  OrderFeature,
  PairedCostAdjustmentType,
  SubjectiveCharacteristic,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';

const THREE_TIER_QUALITY_LABELS = ['-', 'Gd', 'Avg', 'Low'];
const THREE_TIER_POOL_QUALITY_LABELS = ['-', 'Lux', 'Better', 'Std'];

const areaAndQualityDetails = (
  feature?: HasFeatureAreaAndQuality,
  labels: string[] = THREE_TIER_QUALITY_LABELS,
) => {
  if (isDefined(feature) && isDefined(feature.area) && isDefined(feature.quality)) {
    return `${feature.area.unitAmount ?? '-'} sf, ${labels[feature.quality]}`;
  }

  return '';
};

export const DEPRECIATION = {
  functional: {
    hasDepreciation: ({ functionalDepreciation }: Improvements) =>
      isDefined(functionalDepreciation) && functionalDepreciation.unitAmount > 0,
  },
  external: {
    hasDepreciation: ({ externalDepreciation }: Improvements) =>
      isDefined(externalDepreciation) && externalDepreciation.unitAmount > 0,
  },
};
export type DepreciationType = keyof typeof DEPRECIATION;

/** @deprecated Use {@link ORDER_FEATURES} instead. */
export type Feature = OrderFeature | 'belowGradeBath' | 'finishedBasement' | 'totalBasement';
/** @deprecated Use {@link ORDER_FEATURES} instead. */
export type DetailFeatures = Exclude<Feature, 'basement'>;

type Features = {
  [key in Feature]: {
    label: string;
    hasFeature: (improvements: Improvements) => boolean;
    getDetails?: (improvements: Improvements) => string;
  };
};

// FEATURES are used by the ObjectiveCharacteristics form. The label is the
// label that will show in the disclosure modal, and hasFeature is what
// determines if a feature should initially be disclosed.
// This is also used by CostData to determine if we should show cost data for
// any particular feature.

/** @deprecated Use {@link ORDER_FEATURES} instead. */
export const FEATURES: Features = {
  garage: {
    label: 'Attached Garage',
    hasFeature: ORDER_FEATURES.garage.hasFeature,
    getDetails: ({ garage }: Improvements) => {
      let unitAmount: number = 0;
      if (garage?.area) unitAmount += garage.area.unitAmount;

      if (unitAmount > 0) return `${unitAmount} sf`;
      return '';
    },
  },
  detachedGarage: {
    label: 'Detached Garage',
    hasFeature: ORDER_FEATURES.detachedGarage.hasFeature,
    getDetails: ({ detachedGarage }: Improvements) => {
      if (detachedGarage?.area) return `${detachedGarage.area.unitAmount ?? '-'} sf`;
      return '';
    },
  },
  carport: {
    label: 'Carport',
    hasFeature: ORDER_FEATURES.carport.hasFeature,
    getDetails: ({ carport }: Improvements) => {
      if (carport?.area) return `${carport.area.unitAmount ?? '-'} sf`;
      return '';
    },
  },
  basement: {
    label: 'Basement',
    hasFeature: ORDER_FEATURES.basement.hasFeature,
    getDetails: undefined,
  },
  finishedBasement: {
    label: 'Bsmt Fin',
    hasFeature: ({ basement }: Improvements) =>
      isDefined(basement) && basement.hasFeature && isDefined(basement.finishedArea),
    getDetails: ({ basement }: Improvements) =>
      isDefined(basement?.finishedArea) && (basement?.finishedArea.unitAmount ?? 0) > 0
        ? AloftArea.schema.parse(basement?.finishedArea).toString().replace('sqft', 'sf')
        : '-',
  },
  totalBasement: {
    label: 'Bsmt',
    hasFeature: ({ basement }: Improvements) =>
      isDefined(basement) && basement.hasFeature && isDefined(basement.totalArea),
    getDetails: ({ basement }: Improvements) =>
      isDefined(basement?.totalArea) && (basement?.totalArea.unitAmount ?? 0) > 0
        ? AloftArea.schema.parse(basement?.totalArea).toString().replace('sqft', 'sf')
        : '-',
  },
  belowGradeBath: {
    label: 'Bsmt Baths',
    hasFeature: ({ basement }: Improvements) =>
      isDefined(basement) &&
      basement.hasFeature &&
      (isDefined(basement.fullBaths) || isDefined(basement.halfBaths)),
    getDetails: ({ basement }: Improvements) =>
      isDefined(basement?.fullBaths) || isDefined(basement?.halfBaths)
        ? `${basement?.fullBaths ?? 0}.${basement?.halfBaths ?? 0}`
        : '-',
  },
  attic: {
    label: 'Attic',
    hasFeature: ORDER_FEATURES.attic.hasFeature,
    getDetails: ({ attic }: Improvements) => {
      if (attic?.area) return AloftArea.schema.parse(attic.area).toString().replace('sqft', 'sf');
      return '';
    },
  },
  fireplace: {
    label: 'Fireplace',
    hasFeature: ORDER_FEATURES.fireplace.hasFeature,
    getDetails: ({ fireplace }: Improvements) => fireplace?.count?.toString() ?? '',
  },
  shed: {
    label: 'Shed',
    hasFeature: ORDER_FEATURES.shed.hasFeature,
    getDetails: ({ shed }: Improvements) => areaAndQualityDetails(shed),
  },
  workshop: {
    label: 'Workshop',
    hasFeature: ORDER_FEATURES.workshop.hasFeature,
    getDetails: ({ workshop }: Improvements) => areaAndQualityDetails(workshop),
  },
  barn: {
    label: 'Barn',
    hasFeature: ORDER_FEATURES.barn.hasFeature,
    getDetails: ({ barn }: Improvements) => areaAndQualityDetails(barn),
  },
  pool: {
    label: 'Pool',
    hasFeature: ORDER_FEATURES.pool.hasFeature,
    getDetails: ({ pool }: Improvements) =>
      areaAndQualityDetails(pool, THREE_TIER_POOL_QUALITY_LABELS),
  },
  spa: {
    label: 'Spa',
    hasFeature: ORDER_FEATURES.spa.hasFeature,
    getDetails: ({ spa }: Improvements) =>
      areaAndQualityDetails(spa, THREE_TIER_POOL_QUALITY_LABELS),
  },
  adu: {
    label: 'ADU',
    hasFeature: ORDER_FEATURES.adu.hasFeature,
    getDetails: ({ adu }: Improvements) => {
      if (adu && adu.area && adu.quality) {
        return `${AloftArea.schema.parse(adu.area).toString()}, ${
          adu.halfBaths ?? 0
        } Half Baths, ${adu.fullBaths ?? 0} Full Baths, Q${adu.quality}`;
      }

      return '';
    },
  },
  porch: {
    label: 'Porch',
    hasFeature: ORDER_FEATURES.porch.hasFeature,
    getDetails: ({ porch }: Improvements) => {
      if (porch?.area) return AloftArea.schema.parse(porch.area).toString().replace('sqft', 'sf');
      return '';
    },
  },
  patio: {
    label: 'Patio',
    hasFeature: ORDER_FEATURES.patio.hasFeature,
    getDetails: ({ patio }: Improvements) => {
      if (patio?.area) return AloftArea.schema.parse(patio.area).toString().replace('sqft', 'sf');
      return '';
    },
  },
  deck: {
    label: 'Deck',
    hasFeature: ORDER_FEATURES.deck.hasFeature,
    getDetails: ({ deck }: Improvements) => {
      if (deck?.area) return AloftArea.schema.parse(deck.area).toString().replace('sqft', 'sf');
      return '';
    },
  },
  manualCost: {
    label: 'Manual Cost',
    hasFeature: ORDER_FEATURES.manualCost.hasFeature,
    getDetails: () => '',
  },
  other: {
    label: 'Other',
    hasFeature: ORDER_FEATURES.other.hasFeature,
    getDetails: () => '',
  },
  solarPanel: {
    label: 'Solar Panel',
    hasFeature: ORDER_FEATURES.solarPanel.hasFeature,
    getDetails: ({ solarPanel }: Improvements) => {
      if (solarPanel?.ownership) {
        return solarPanel.ownership === 'leased' ? 'Leased' : 'Owned';
      }
      return 'Ownership unknown';
    },
  },
};

export const DISCLOSABLE_FEATURES = [
  'adu',
  'attic',
  'barn',
  'basement',
  'carport',
  'deck',
  'fireplace',
  'garage',
  'detachedGarage',
  'manualCost',
  'other',
  'patio',
  'pool',
  'porch',
  'shed',
  'solarPanel',
  'spa',
  'workshop',
] as const;
export type DisclosableFeature = (typeof DISCLOSABLE_FEATURES)[number];

export const FEATURE_ORDER: { [key in PairedCostAdjustmentType]?: DetailFeatures[] } = {
  basement: ['totalBasement', 'finishedBasement'],
  bathBelowGrade: ['belowGradeBath'],
  garageCarport: ['carport', 'garage', 'detachedGarage'],
  porchPatioDeck: ['porch', 'patio', 'deck'],
  pool: ['pool', 'spa'],
  shed: ['shed'],
  workshop: ['workshop'],
  barn: ['barn'],
  adu: ['adu'],
  attic: ['attic'],
  fireplace: ['fireplace'],
  manual: ['manualCost'],
};

const ADDITIONAL_STRUCTURES_QUALITY_LABELS = [
  'Luxury',
  'Semi-Luxury',
  'Best Standard',
  'Good Standard',
  'Average Standard',
  'Minimum Standard',
];

export function getAdditionalStructuresQualityLabel(qualityRating?: number) {
  if (isUndefined(qualityRating)) return '-';
  return ADDITIONAL_STRUCTURES_QUALITY_LABELS[qualityRating - 1] ?? '-';
}
export type DepreciationSegment = {
  effectiveAgeField: SubjectiveCharacteristic;
  ecoLifeField: EcoLife;
  label?: string;
  orderFeature: DetailFeatures | null;
  enabled?: boolean;
  defaultEcoLifeField: DefaultEcoLifeField;
};

export const DEPRECIATION_SEGMENTS: DepreciationSegment[] = [
  {
    effectiveAgeField: 'garageEffAge',
    ecoLifeField: 'garageEcoLife',
    label: 'Garage',
    orderFeature: 'garage',
    defaultEcoLifeField: 'defaultEcoLifeGarage',
  },
  {
    effectiveAgeField: 'detachedGarageEffAge',
    ecoLifeField: 'detachedGarageEcoLife',
    label: 'Detached Garage',
    orderFeature: 'detachedGarage',
    defaultEcoLifeField: 'defaultEcoLifeDetachedGarage',
  },
  {
    effectiveAgeField: 'carportEffAge',
    ecoLifeField: 'carportEcoLife',
    label: 'Carport',
    orderFeature: 'carport',
    defaultEcoLifeField: 'defaultEcoLifeCarport',
  },
  {
    effectiveAgeField: 'porchEffAge',
    ecoLifeField: 'porchEcoLife',
    label: 'Porch',
    orderFeature: 'porch',
    defaultEcoLifeField: 'defaultEcoLifePorch',
  },
  {
    effectiveAgeField: 'patioEffAge',
    ecoLifeField: 'patioEcoLife',
    label: 'Patio',
    orderFeature: 'patio',
    defaultEcoLifeField: 'defaultEcoLifePatio',
  },
  {
    effectiveAgeField: 'deckEffAge',
    ecoLifeField: 'deckEcoLife',
    label: 'Deck',
    orderFeature: 'deck',
    defaultEcoLifeField: 'defaultEcoLifeDeck',
  },
  {
    effectiveAgeField: 'workshopEffAge',
    ecoLifeField: 'workshopEcoLife',
    label: 'Workshop',
    orderFeature: 'workshop',
    defaultEcoLifeField: 'defaultEcoLifeWorkshop',
  },
  {
    effectiveAgeField: 'shedEffAge',
    ecoLifeField: 'shedEcoLife',
    label: 'Shed',
    orderFeature: 'shed',
    defaultEcoLifeField: 'defaultEcoLifeShed',
  },
  {
    effectiveAgeField: 'barnEffAge',
    ecoLifeField: 'barnEcoLife',
    label: 'Barn',
    orderFeature: 'barn',
    defaultEcoLifeField: 'defaultEcoLifeBarn',
  },
  {
    effectiveAgeField: 'poolEffAge',
    ecoLifeField: 'poolEcoLife',
    label: 'Pool',
    orderFeature: 'pool',
    defaultEcoLifeField: 'defaultEcoLifePool',
  },
  {
    effectiveAgeField: 'spaEffAge',
    ecoLifeField: 'spaEcoLife',
    label: 'Spa',
    orderFeature: 'spa',
    defaultEcoLifeField: 'defaultEcoLifeSpa',
  },
  {
    effectiveAgeField: 'aduEffAge',
    ecoLifeField: 'aduEcoLife',
    label: 'ADU',
    orderFeature: 'adu',
    defaultEcoLifeField: 'defaultEcoLifeADU',
  },
  {
    effectiveAgeField: 'fireplaceEffAge',
    ecoLifeField: 'fireplaceEcoLife',
    label: 'Fireplace',
    orderFeature: 'fireplace',
    defaultEcoLifeField: 'defaultEcoLifeFireplace',
  },
  {
    effectiveAgeField: 'manualCostEffAge',
    ecoLifeField: 'manualCostEcoLife',
    label: 'Manual Cost',
    orderFeature: 'manualCost',
    defaultEcoLifeField: 'defaultEcoLifeManualCost',
  },
];
